import { useEffect, useState } from 'react'
import { Copy, Download, Sparkles } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Chip } from '@/components/ui/Chip'
import { Field, Input, Select, Textarea } from '@/components/ui/Field'
import { Drawer } from '@/components/ui/Overlay'
import { FavoriteToggle } from '@/components/review/FavoriteToggle'
import { TagEditor } from '@/components/review/TagEditor'
import { useEntityDraft } from '@/components/review/useEntityDraft'
import { CopyVersionList } from './CopyVersionList'
import { COPY_TYPES } from '@/lib/constants'
import { downloadBlob, slugify } from '@/lib/csv'
import type { CopyDoc, CopyType } from '@/types'

interface Draft {
  name: string
  copyType: CopyType
  body: string
  tags: string[]
}

const DEFAULTS: Draft = { name: '', copyType: 'other', body: '', tags: [] }

/**
 * Not a review surface — no approve/reject, so this uses `Drawer` directly
 * rather than `ReviewDrawer`.
 *
 * The body is a plain textarea with no rich-text editor, deliberately: the
 * copy is consumed as text by the agent and by whatever CMS it lands in, and
 * a WYSIWYG only introduces markup nobody wants (docs/03-page-specs.md §9.4).
 */
export function CopyDrawer({
  doc,
  clientName,
  tagPool,
  onClose,
  onSave,
  onFavorite,
  onDelete,
  onToast,
}: {
  doc: CopyDoc | 'new' | null
  clientName: string
  tagPool: string[]
  onClose: () => void
  onSave: (patch: Partial<CopyDoc> & { id?: string }) => Promise<void>
  onFavorite: (id: string, favorite: boolean) => void
  onDelete: (doc: CopyDoc) => void
  onToast: (message: string) => void
}) {
  const creating = doc === 'new'
  const existing = creating ? null : doc

  const [editMode, setEditMode] = useState(false)
  const [viewingVersion, setViewingVersion] = useState<number | null>(null)

  const draft = useEntityDraft<Draft>(
    existing
      ? {
          name: existing.name,
          copyType: existing.copyType,
          body: existing.body,
          tags: existing.tags,
        }
      : null,
    DEFAULTS,
  )

  const { reset } = draft
  useEffect(() => {
    if (!doc) {
      reset()
      setEditMode(false)
      setViewingVersion(null)
    }
  }, [doc, reset])

  if (!doc) return null

  function close() {
    draft.reset()
    setEditMode(false)
    setViewingVersion(null)
    onClose()
  }

  const editing = creating || editMode
  const shownBody =
    viewingVersion !== null
      ? (existing?.history.find((h) => h.version === viewingVersion)?.body ?? '')
      : (existing?.body ?? '')

  /**
   * Replaces the mockup's "Open in Chat", which is dead without an in-portal
   * chat. A clipboard block and nothing more — no MCP round-trip, no state.
   * The trailing instruction line is left for the user to finish.
   */
  function copyPromptStarter() {
    if (!existing) return
    const block = [
      `Client: ${clientName}`,
      `Asset: ${existing.name} (v${existing.version})`,
      '',
      existing.body,
      '',
      '---',
      'Revise this for ',
    ].join('\n')
    navigator.clipboard?.writeText(block)
    onToast('Prompt starter copied. Paste it into Claude.')
  }

  return (
    <Drawer
      open
      onClose={close}
      width="w-[680px]"
      title={creating ? 'New copy' : (existing?.name ?? '')}
      subtitle={
        existing && (
          <span className="flex flex-wrap items-center gap-2 text-ink-faint">
            <span className="tabular">v{existing.version}</span>
            <span>·</span>
            <span>{existing.author}</span>
            <span>·</span>
            <span>{existing.origin === 'mcp' ? 'Saved from Claude' : 'Added by hand'}</span>
          </span>
        )
      }
      footer={
        editing ? (
          <>
            <Button
              size="sm"
              onClick={() => {
                draft.reset()
                if (creating) close()
                else setEditMode(false)
              }}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              variant="primary"
              onClick={() =>
                void onSave({ ...(existing ? { id: existing.id } : {}), ...draft.value })
              }
            >
              {creating ? 'Add copy' : 'Save new version'}
            </Button>
          </>
        ) : (
          <>
            {existing && (
              <Button
                size="sm"
                variant="ghost"
                className="mr-auto text-brand-red-700"
                onClick={() => onDelete(existing)}
              >
                Delete
              </Button>
            )}
            <Button size="sm" onClick={() => setEditMode(true)}>
              Edit
            </Button>
            <Button size="sm" variant="primary" onClick={copyPromptStarter}>
              <Sparkles size={13} aria-hidden />
              Copy as prompt starter
            </Button>
          </>
        )
      }
    >
      {editing ? (
        <div className="space-y-4">
          <Field label="Name" required>
            {(p) => (
              <Input {...p} value={draft.value.name} onChange={(e) => draft.set('name', e.target.value)} />
            )}
          </Field>

          <Field label="Type">
            {(p) => (
              <Select
                {...p}
                value={draft.value.copyType}
                onChange={(e) => draft.set('copyType', e.target.value as CopyType)}
              >
                {COPY_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          <Field
            label="Copy"
            required
            hint="Plain text. It is consumed as text by the agent and by whatever the copy lands in."
          >
            {(p) => (
              <Textarea
                {...p}
                rows={14}
                value={draft.value.body}
                className="text-[14px] leading-7"
                onChange={(e) => draft.set('body', e.target.value)}
              />
            )}
          </Field>

          <div className="space-y-1.5">
            <span className="field-label block">Tags</span>
            <TagEditor
              tags={draft.value.tags}
              suggestions={tagPool}
              onChange={(tags) => draft.set('tags', tags)}
            />
          </div>
        </div>
      ) : (
        existing && (
          <div className="space-y-5">
            <div className="flex flex-wrap items-center gap-2">
              <FavoriteToggle
                favorite={existing.favorite}
                onChange={(next) => onFavorite(existing.id, next)}
              />
              {existing.tags.map((t) => (
                <Chip key={t} label={t} />
              ))}
              <span className="ml-auto flex gap-1.5">
                <Button
                  size="sm"
                  onClick={() => {
                    navigator.clipboard?.writeText(shownBody)
                    onToast('Copy copied to clipboard.')
                  }}
                >
                  <Copy size={13} aria-hidden />
                  Copy
                </Button>
                <Button
                  size="sm"
                  onClick={() =>
                    downloadBlob(shownBody, `${slugify(existing.name)}.md`, 'text/markdown')
                  }
                >
                  <Download size={13} aria-hidden />
                  Download
                </Button>
              </span>
            </div>

            {viewingVersion !== null && viewingVersion !== existing.version && (
              <div className="flex items-center gap-2 rounded-[4px] bg-warn-50 px-3 py-2 text-[13px] text-warn">
                <span>You are reading v{viewingVersion}, not the current version.</span>
                <button
                  type="button"
                  onClick={() => setViewingVersion(null)}
                  className="ml-auto underline underline-offset-2"
                >
                  Back to v{existing.version}
                </button>
              </div>
            )}

            <article className="whitespace-pre-wrap rounded-[6px] border border-rule bg-canvas px-4 py-4 text-[14px] leading-7">
              {shownBody}
            </article>

            <CopyVersionList
              history={existing.history}
              currentVersion={existing.version}
              viewing={viewingVersion}
              onView={setViewingVersion}
            />
          </div>
        )
      )}
    </Drawer>
  )
}
